import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Flag, AlertTriangle, ShieldOff, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';

const REASON_LABELS = {
  inappropriate: 'Inappropriate behavior',
  spam: 'Spam',
  fake_profile: 'Fake profile',
  other: 'Other',
};

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800',
  reviewed: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  dismissed: 'bg-gray-100 text-text-mid',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

export default function AdminReportsTab({ getAuthHeader }) {
  const [data, setData] = useState({ reports: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionDialog, setActionDialog] = useState(null); // { report, action }
  const [actionNotes, setActionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/safety/reports${filter ? `?status=${filter}` : ''}`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const markReviewed = async (r) => {
    try {
      await api.patch(`/admin/safety/reports/${r.id}`, { status: 'reviewed' }, { headers: getAuthHeader() });
      toast.success('Marked as reviewed');
      await load();
    } catch (err) { toast.error('Failed'); }
  };

  const dismiss = async (r) => {
    try {
      await api.patch(`/admin/safety/reports/${r.id}`, { status: 'dismissed' }, { headers: getAuthHeader() });
      toast.success('Report dismissed');
      await load();
    } catch (err) { toast.error('Failed'); }
  };

  const submitAction = async () => {
    if (!actionDialog) return;
    setSubmitting(true);
    try {
      await api.post(
        `/admin/safety/reports/${actionDialog.report.id}/action`,
        { action: actionDialog.action, notes: actionNotes },
        { headers: getAuthHeader() },
      );
      toast.success(`Member ${actionDialog.action}ed`);
      setActionDialog(null);
      setActionNotes('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const summary = data.summary || {};

  return (
    <div className="space-y-5" data-testid="admin-reports-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <Flag className="text-red-600" size={20} />
          </div>
          <div>
            <h2 className="font-display text-2xl text-navy">Reports Queue</h2>
            <p className="text-xs text-text-mid">Member reports flagged for safety review.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="reports-summary">
        {[
          { k: 'pending', label: 'Pending', cls: 'border-amber-300' },
          { k: 'reviewed', label: 'Reviewed', cls: 'border-blue-300' },
          { k: 'resolved', label: 'Resolved', cls: 'border-green-300' },
          { k: 'dismissed', label: 'Dismissed', cls: 'border-gray-300' },
        ].map((c) => (
          <button
            key={c.k}
            onClick={() => setFilter(c.k)}
            className={`bg-white rounded-2xl shadow-card p-4 border-2 text-left transition-colors ${filter === c.k ? c.cls : 'border-transparent hover:border-border-subtle'}`}
            data-testid={`filter-${c.k}-card`}
          >
            <p className="text-xs text-text-mid">{c.label}</p>
            <p className="text-2xl font-bold text-navy">{summary[c.k] || 0}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-border-subtle overflow-hidden">
        {loading ? (
          <p className="p-6 text-text-mid text-sm"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading…</p>
        ) : (data.reports || []).length === 0 ? (
          <p className="p-8 text-center text-text-light text-sm" data-testid="reports-empty">No {filter} reports.</p>
        ) : (
          <Table data-testid="reports-table">
            <TableHeader>
              <TableRow>
                <TableHead>Reported</TableHead>
                <TableHead>Reporter</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.reports || []).map((r) => (
                <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                  <TableCell>
                    <div className="text-sm text-navy font-medium">{r.reported_name || '—'}</div>
                    <div className="text-[11px] text-text-light">{r.reported_email}</div>
                    {r.reported_account_status && r.reported_account_status !== 'active' && (
                      <Badge className="mt-1 bg-red-100 text-red-700 text-[10px]">{r.reported_account_status}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-navy">{r.reporter_name || '—'}</div>
                    <div className="text-[11px] text-text-light">{r.reporter_email}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-cream text-navy">{REASON_LABELS[r.reason] || r.reason}</Badge>
                    {r.details && (
                      <p className="text-[11px] text-text-mid mt-1 line-clamp-2 max-w-xs">{r.details}</p>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-text-mid">{fmt(r.created_at)}</TableCell>
                  <TableCell>
                    <Badge className={STATUS_BADGE[r.status] || 'bg-gray-100'}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`report-actions-${r.id}`}>
                          <MoreVertical size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        {r.status === 'pending' && (
                          <DropdownMenuItem onClick={() => markReviewed(r)}>
                            Mark as Reviewed
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => { setActionDialog({ report: r, action: 'warn' }); setActionNotes(''); }}
                          className="text-amber-700"
                          data-testid={`action-warn-${r.id}`}
                        >
                          <AlertTriangle size={12} className="mr-2" /> Warn member
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => { setActionDialog({ report: r, action: 'suspend' }); setActionNotes(''); }}
                          className="text-orange-700"
                          data-testid={`action-suspend-${r.id}`}
                        >
                          <ShieldOff size={12} className="mr-2" /> Suspend
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => { setActionDialog({ report: r, action: 'disable' }); setActionNotes(''); }}
                          className="text-red-700"
                          data-testid={`action-disable-${r.id}`}
                        >
                          <ShieldOff size={12} className="mr-2" /> Remove (disable)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => dismiss(r)} data-testid={`action-dismiss-${r.id}`}>
                          Dismiss report
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!actionDialog} onOpenChange={(v) => !v && setActionDialog(null)}>
        <DialogContent className="max-w-md" data-testid="report-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy capitalize">
              {actionDialog?.action} {actionDialog?.report?.reported_name}?
            </DialogTitle>
            <DialogDescription>
              {actionDialog?.action === 'warn' && 'Records a warning on this member. No account change.'}
              {actionDialog?.action === 'suspend' && 'Temporarily suspends this account. They cannot sign in.'}
              {actionDialog?.action === 'disable' && 'Disables the account permanently. Member is removed from the community.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            placeholder="Optional admin notes (not visible to the member)"
            rows={3}
            maxLength={2000}
            className="rounded-xl"
            data-testid="report-action-notes"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button
              onClick={submitAction}
              disabled={submitting}
              className={`${actionDialog?.action === 'disable' ? 'bg-red-600 hover:bg-red-700' : actionDialog?.action === 'suspend' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-amber-600 hover:bg-amber-700'} text-white rounded-full`}
              data-testid="confirm-report-action-btn"
            >
              {submitting && <Loader2 size={14} className="animate-spin mr-1" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
